import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { m } from '@/paraglide/messages';
import { captureConsentChoice } from '@/lib/analytics';
import {
  consentDecision,
  recordConsent,
  subscribeConsent,
  subscribeConsentSettings,
  type ConsentDecision,
} from '@/lib/consent';
import { applyConsent } from '@/lib/posthog';

// The consent banner (OH-72). Shown until the guest chooses, and reachable
// again for ever after through the footer's "Cookie settings" link — a choice
// you cannot revisit is not really a choice (GDPR Art. 7(3)).
//
// NO INLINE STYLES. The site's CSP is being measured with `style-src 'self'`
// and no 'unsafe-inline' (OH-119): a `style={{…}}` here would produce
// violation reports that read as evidence the allowance is still needed, and
// quietly corrupt that measurement. Tailwind classes only.
export function ConsentBanner() {
  const [decision, setDecision] = useState<ConsentDecision>(() => consentDecision());
  // Reopened from the footer, as opposed to shown because no choice exists yet.
  // The distinction matters for focus: see the effect below.
  const [reopened, setReopened] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Computed before the effects below, which depend on it. Early-returning
  // null happens after all hooks have run — React requires a stable hook order.
  const [persistFailed, setPersistFailed] = useState(false);
  const visible = decision === 'pending' || reopened;

  useEffect(
    () =>
      subscribeConsentSettings(() => {
        setReopened(true);
        setDecision(consentDecision());
      }),
    [],
  );

  // A choice made in another tab reaches this one when it is brought back to
  // the front (lib/posthog.ts re-reads the cookie on focus). Follow it, or
  // this tab keeps showing a banner for a question the guest already answered
  // — and, worse, would let them answer it twice with different answers.
  useEffect(
    () =>
      subscribeConsent((next) => {
        setDecision(next);
        setReopened(false);
      }),
    [],
  );

  // The banner is fixed to the bottom of the viewport, and it is not the only
  // thing there: the apartment page pins a mobile-only "Check availability"
  // button to the same edge, one z-layer below. Overlapping it would hide the
  // page's primary call to action from every first-time phone visitor — in the
  // pending state, which is where 100% of real traffic starts. So publish the
  // height and let whoever else lives down there move up by it.
  //
  // A CSSOM property write, not a style attribute: the site's CSP is being
  // measured with `style-src 'self'` (OH-119), and this is the same mechanism
  // React already uses for the ~37 inline-style sites on an apartment page.
  useEffect(() => {
    const panel = panelRef.current;
    if (!visible || !panel) {
      document.documentElement.style.removeProperty('--consent-banner-height');
      return;
    }
    const publish = () => {
      document.documentElement.style.setProperty(
        '--consent-banner-height',
        `${panel.offsetHeight}px`,
      );
    };
    publish();
    // The copy wraps differently at every width, so a one-off measurement goes
    // stale on rotation or a resize. Guarded because jsdom has no
    // ResizeObserver at all — same precedent as DateRangePicker. A single
    // measurement is the right degradation: the value is still published, it
    // just stops tracking.
    if (typeof ResizeObserver === 'undefined') {
      return () => document.documentElement.style.removeProperty('--consent-banner-height');
    }
    const observer = new ResizeObserver(publish);
    observer.observe(panel);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--consent-banner-height');
    };
  }, [visible]);

  useEffect(() => {
    // Move focus ONLY when the guest asked for this panel. On first load the
    // banner appears unbidden, and yanking focus out of the page a visitor is
    // already reading is hostile — and would land a screen-reader user in a
    // dialog they did not open. Non-modal either way: the page stays usable,
    // which is what makes "no choice yet" a tolerable state rather than a wall.
    if (reopened) panelRef.current?.focus();
  }, [reopened]);


  if (!visible) return null;

  function choose(analytics: boolean) {
    // Captured while the decision is still `pending` — i.e. in cookieless
    // mode, where sending is lawful and nothing is stored. This is the only
    // window in which the choice itself is reportable, and without it a stuck
    // banner, a browser that refuses cookies, and a genuinely high refusal
    // rate all look identical from the operator's chair: an absence.
    captureConsentChoice(analytics, reopened);

    const next = analytics ? 'granted' : 'denied';
    const persisted = recordConsent(analytics);

    // React state FIRST. applyConsent talks to a vendor and to browser
    // storage; if anything in there throws, the choice must still be recorded
    // on screen. Otherwise the cookie says "denied", the gate honours it, and
    // the guest sits in front of a Reject button that visibly does nothing.
    setDecision(next);
    setReopened(false);
    setPersistFailed(!persisted);

    try {
      applyConsent(next);
    } catch {
      // The gate does not depend on this: before_send already reads the live
      // decision, so events stop either way. What may not have happened is the
      // sweep of already-written storage.
    }
  }

  return (
    <div
      ref={panelRef}
      // A landmark, not a dialog. This panel never traps focus and never
      // blocks the page, and announcing a "dialog" that the user cannot be
      // inside of — and did not open — misdescribes it to a screen reader.
      // As a named region it still shows up in the landmarks list, which is
      // how someone navigating by landmark reaches the choice on purpose.
      // (It also stopped colliding with getByRole('dialog') in tests that
      // mount the real route tree — a symptom, not the reason.)
      role="region"
      aria-labelledby="consent-title"
      aria-describedby="consent-body"
      tabIndex={-1}
      data-testid="consent-banner"
      className={cn(
        'fixed inset-x-0 bottom-0 z-50',
        'border-t border-border bg-card shadow-[var(--shadow-md)]',
        'px-5 py-5 md:px-8',
      )}
    >
      <div className="mx-auto flex max-w-[1100px] flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-8">
        <div className="max-w-[640px]">
          <p id="consent-title" className="font-semibold text-primary">
            {m.consent_title()}
          </p>
          <p id="consent-body" className="mt-1 text-sm leading-relaxed text-muted">
            {m.consent_body()}{' '}
            <Link
              to="/legal/$slug"
              params={{ slug: 'privacy' }}
              className="underline underline-offset-2 hover:text-primary"
            >
              {m.consent_privacy_link()}
            </Link>
          </p>
          {reopened && decision !== 'pending' && (
            <p className="mt-2 text-sm text-muted" data-testid="consent-current-state">
              {decision === 'granted' ? m.consent_state_granted() : m.consent_state_denied()}
            </p>
          )}
          {/* Say so rather than re-asking on every page as if nothing had
              happened. The choice still holds for this tab — `current` in
              consent.ts is in memory and the gate reads it — but we cannot
              promise it will survive the next load, so we do not. */}
          {persistFailed && (
            <p className="mt-2 text-sm text-destructive" data-testid="consent-not-persisted">
              {m.consent_not_persisted()}
            </p>
          )}
        </div>

        {/* Two buttons, identical in size, variant and prominence. A "Reject"
            styled quieter than "Accept" is the dark pattern regulators name
            first; making them the same removes the nudge instead of arguing
            about how big a nudge is allowed. */}
        <div className="flex shrink-0 gap-3">
          <Button
            variant="secondary"
            size="md"
            className="w-[11rem] flex-1 md:flex-none"
            data-testid="consent-reject"
            onClick={() => choose(false)}
          >
            {m.consent_reject()}
          </Button>
          <Button
            variant="secondary"
            size="md"
            className="w-[11rem] flex-1 md:flex-none"
            data-testid="consent-accept"
            onClick={() => choose(true)}
          >
            {m.consent_accept()}
          </Button>
        </div>
      </div>
    </div>
  );
}
